/**
 * Structured HTML for LangChain run file drawer: execution_report.json and pipeline_results.json.
 * Exposed on window for inline scripts in langchain/runs/_show.html.erb.
 */

function escapeHtml(s) {
  if (s == null || s === '') return ''
  const str = String(s)
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function fmtNum(n) {
  if (n == null || n === '') return '—'
  const x = Number(n)
  if (Number.isNaN(x)) return escapeHtml(String(n))
  return x.toLocaleString()
}

function fmtUsd(n) {
  if (n == null || n === '') return '—'
  const x = Number(n)
  if (Number.isNaN(x)) return escapeHtml(String(n))
  return `$${x.toFixed(4)}`
}

function statusBadgeClass(status) {
  const s = String(status || '').toLowerCase()
  if (s === 'success' || s === 'succeeded' || s === 'ok') {
    return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
  }
  if (s === 'failed' || s === 'error') {
    return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
  }
  if (s.includes('error') || s.includes('fail')) {
    return 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200'
  }
  return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
}

function card(title, innerHtml) {
  return `
    <section class="rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/50 overflow-hidden mb-4">
      <h4 class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 px-3 py-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
        ${escapeHtml(title)}
      </h4>
      <div class="px-3 py-3 text-sm text-gray-800 dark:text-gray-200">
        ${innerHtml}
      </div>
    </section>
  `
}

function statGrid(pairs) {
  const cells = pairs
    .map(
      ([label, value]) => `
      <div class="rounded-md bg-gray-50 dark:bg-gray-900/60 px-2 py-2">
        <div class="text-xs text-gray-500 dark:text-gray-400">${escapeHtml(label)}</div>
        <div class="font-mono text-sm font-medium text-gray-900 dark:text-gray-100">${value}</div>
      </div>
    `
    )
    .join('')
  return `<div class="grid grid-cols-2 sm:grid-cols-3 gap-2">${cells}</div>`
}

function isDigestibleExecutionReport(data) {
  if (!data || typeof data !== 'object') return false
  if (typeof data.graph_name !== 'string' || !data.summary || typeof data.summary !== 'object') {
    return false
  }
  return (
    data.report_version === '1.0' ||
    Array.isArray(data.node_executions) ||
    data.summary.total_tokens != null ||
    data.summary.total_llm_calls != null
  )
}

function isDigestiblePipelineResults(data) {
  if (!data || typeof data !== 'object') return false
  const hasNbib = data.nbib_parsing && typeof data.nbib_parsing === 'object'
  const hasScreening = data.screening && typeof data.screening === 'object'
  if (!hasNbib && !hasScreening) return false
  if (data.pipeline === 'nbib_screening') return true
  if (hasScreening && Array.isArray(data.screening.results)) return true
  return hasNbib
}

/** Rails preview sometimes returns { content: string, type: "json" } instead of raw body */
function tryUnwrapPreviewEnvelope(parsed) {
  if (!parsed || typeof parsed !== 'object') return parsed
  if (parsed.report_version || parsed.graph_name != null || parsed.pipeline != null) return parsed
  const c = parsed.content
  const t = parsed.type
  if (typeof c !== 'string' || (t !== 'json' && t !== 'text')) return parsed
  try {
    return JSON.parse(c)
  } catch {
    return parsed
  }
}

/**
 * @param {string | object} raw — JSON string, preview envelope, or object from inlined metadata
 * @returns {{ parsed: object | null, pretty: string, parseError: Error | null }}
 */
function prepareJsonDrawerPayload(raw) {
  let parsed
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw)
    } catch (e) {
      return { parsed: null, pretty: raw, parseError: e }
    }
  } else if (raw && typeof raw === 'object') {
    parsed = raw
  } else {
    return { parsed: null, pretty: String(raw), parseError: new Error('Invalid JSON payload') }
  }
  parsed = tryUnwrapPreviewEnvelope(parsed)
  let pretty
  try {
    pretty = JSON.stringify(parsed, null, 2)
  } catch (e) {
    return { parsed: null, pretty: typeof raw === 'string' ? raw : String(raw), parseError: e }
  }
  return { parsed, pretty, parseError: null }
}

function renderExecutionReportV1(data) {
  const summary = data.summary || {}
  const stages = summary.pipeline_stages || {}
  const screening = stages.screening || {}
  const nbib = stages.nbib_parsing || {}
  const criteria = stages.criteria_loading || {}
  const costByModel = summary.cost_by_model || {}
  const parallelism = summary.parallelism || null

  let stagesHtml = ''
  if (Object.keys(stages).length) {
    const parts = []
    if (criteria && Object.keys(criteria).length) {
      parts.push(
        statGrid([
          ['Source', escapeHtml(criteria.source || '—')],
          ['Inclusion criteria', fmtNum(criteria.inclusion_count)],
          ['Exclusion criteria', fmtNum(criteria.exclusion_count)],
        ])
      )
    }
    if (nbib && Object.keys(nbib).length) {
      parts.push(
        statGrid([
          ['Records parsed', fmtNum(nbib.records_parsed)],
          ['Records skipped', fmtNum(nbib.records_skipped)],
          ['Articles created', fmtNum(nbib.articles_created)],
          ['Warnings', fmtNum(nbib.warnings)],
        ])
      )
    }
    if (screening && Object.keys(screening).length) {
      parts.push(
        statGrid([
          ['Articles screened', fmtNum(screening.articles_screened)],
          ['Included', fmtNum(screening.included)],
          ['Excluded', fmtNum(screening.excluded)],
          ['Human review', fmtNum(screening.human_review)],
          ['Skipped articles', fmtNum(screening.articles_skipped)],
          ['Max workers', fmtNum(screening.max_workers)],
        ])
      )
    }
    stagesHtml = card('Pipeline stages', parts.join('<div class="my-3 border-t border-dashed border-gray-200 dark:border-gray-600"></div>'))
  }

  let costRows = ''
  if (costByModel && typeof costByModel === 'object') {
    costRows = Object.entries(costByModel)
      .map(([model, st]) => {
        const stats = st || {}
        return `<tr class="border-t border-gray-100 dark:border-gray-700">
          <td class="py-1.5 pr-2 font-medium">${escapeHtml(model)}</td>
          <td class="py-1.5 pr-2 text-gray-600 dark:text-gray-400">${escapeHtml(stats.provider || '')}</td>
          <td class="py-1.5 text-right font-mono text-xs">${fmtNum(stats.prompt_tokens)} / ${fmtNum(stats.completion_tokens)}</td>
          <td class="py-1.5 text-right font-mono text-xs">${fmtUsd(stats.total_cost_usd)}</td>
          <td class="py-1.5 text-right font-mono text-xs">${fmtNum(stats.total_calls)}</td>
        </tr>`
      })
      .join('')
  }

  const nodes = Array.isArray(data.node_executions) ? data.node_executions : []
  const nodeRows = nodes
    .slice(0, 80)
    .map((n) => {
      const name = n.node || n.name || '—'
      const st = n.status || '—'
      const dur = n.duration_ms != null ? `${fmtNum(n.duration_ms)} ms` : '—'
      const llm = Array.isArray(n.llm_calls) ? n.llm_calls.length : 0
      return `<tr class="border-t border-gray-100 dark:border-gray-700 text-xs">
        <td class="py-1 pr-2 font-mono break-all max-w-[140px]">${escapeHtml(name)}</td>
        <td class="py-1 pr-2"><span class="rounded px-1.5 py-0.5 ${statusBadgeClass(st)}">${escapeHtml(st)}</span></td>
        <td class="py-1 pr-2 font-mono">${dur}</td>
        <td class="py-1 text-right font-mono">${fmtNum(llm)}</td>
      </tr>`
    })
    .join('')
  const nodeMore =
    nodes.length > 80
      ? `<p class="text-xs text-gray-500 mt-2">Showing 80 of ${nodes.length} nodes.</p>`
      : ''

  const errBlock =
    Array.isArray(data.errors) && data.errors.length
      ? card(
          'Errors',
          `<pre class="text-xs overflow-x-auto whitespace-pre-wrap break-words text-red-800 dark:text-red-200 bg-red-50 dark:bg-red-900/20 p-2 rounded">${escapeHtml(JSON.stringify(data.errors, null, 2))}</pre>`
        )
      : ''

  let parHtml = ''
  if (parallelism && typeof parallelism === 'object' && Object.keys(parallelism).length) {
    parHtml = card(
      'Parallelism',
      statGrid([
        ['Max workers', fmtNum(parallelism.max_workers)],
        ['Workers source', escapeHtml(parallelism.workers_source || '—')],
        ['Max LLM concurrency', fmtNum(parallelism.max_llm_concurrency)],
      ])
    )
  }

  return `
    <div class="langchain-report-digest space-y-1">
      <div class="flex flex-wrap items-center gap-2 mb-4">
        <span class="text-lg font-semibold text-gray-900 dark:text-white">Execution report</span>
        <span class="rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(data.status)}">${escapeHtml(data.status || 'unknown')}</span>
      </div>
      <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
        <span class="font-medium text-gray-800 dark:text-gray-200">${escapeHtml(data.graph_name || '')}</span>
        · run <span class="font-mono">${escapeHtml(String(data.run_id || '—'))}</span>
        · ${escapeHtml(String(data.generated_at || '—'))}
        · duration <span class="font-mono">${fmtNum(data.total_duration_ms)} ms</span>
      </p>

      ${card(
        'Summary',
        statGrid([
          ['Nodes executed', fmtNum(summary.nodes_executed)],
          ['Succeeded', fmtNum(summary.nodes_succeeded)],
          ['Failed', fmtNum(summary.nodes_failed)],
          ['Skipped', fmtNum(summary.nodes_skipped)],
          ['LLM calls', fmtNum(summary.total_llm_calls)],
          ['Prompt tokens', fmtNum(summary.total_prompt_tokens)],
          ['Completion tokens', fmtNum(summary.total_completion_tokens)],
          ['Total tokens', fmtNum(summary.total_tokens)],
          ['Est. cost (USD)', fmtUsd(summary.estimated_cost_usd)],
          ['Articles parsed', fmtNum(summary.articles_parsed)],
          ['Articles screened', fmtNum(summary.articles_screened)],
        ])
      )}

      ${stagesHtml}
      ${parHtml}

      ${
        costRows
          ? card(
              'Cost by model',
              `<div class="overflow-x-auto"><table class="w-full text-sm text-left">
                <thead><tr class="text-xs text-gray-500 dark:text-gray-400">
                  <th class="pb-1">Model</th><th class="pb-1">Provider</th><th class="pb-1 text-right">In / Out</th><th class="pb-1 text-right">Cost</th><th class="pb-1 text-right">Calls</th>
                </tr></thead>
                <tbody>${costRows}</tbody>
              </table></div>`
            )
          : ''
      }

      ${card(
        `Node executions (${nodes.length})`,
        nodes.length
          ? `<div class="overflow-x-auto max-h-80 overflow-y-auto"><table class="w-full text-left">
              <thead><tr class="text-xs text-gray-500 dark:text-gray-400"><th class="pb-1">Node</th><th class="pb-1">Status</th><th class="pb-1">Duration</th><th class="pb-1 text-right">LLM</th></tr></thead>
              <tbody>${nodeRows}</tbody>
            </table></div>${nodeMore}`
          : '<p class="text-gray-500 text-sm">No node executions recorded.</p>'
      )}

      ${errBlock}
    </div>
  `
}

function renderPipelineResultsNbib(data) {
  const nb = data.nbib_parsing || {}
  const sc = data.screening || {}
  const results = Array.isArray(sc.results) ? sc.results : []
  const skipped = Array.isArray(sc.skipped_articles) ? sc.skipped_articles : []
  const crit = data.criteria || {}
  const inc = Array.isArray(crit.inclusion) ? crit.inclusion.length : 0
  const exc = Array.isArray(crit.exclusion) ? crit.exclusion.length : 0

  const maxList = 25
  const listItems = results.slice(0, maxList).map((r, i) => {
    const title = (r.article_title || r.title || `Article ${i + 1}`).slice(0, 120)
    const dec = r.decision || '—'
    return `<li class="border-t border-gray-100 dark:border-gray-700 py-1.5 flex flex-wrap gap-2 text-xs">
      <span class="font-mono shrink-0 rounded px-1.5 py-0.5 ${statusBadgeClass(dec === 'include' ? 'success' : dec === 'exclude' ? 'failed' : '')}">${escapeHtml(dec)}</span>
      <span class="text-gray-700 dark:text-gray-300 break-words">${escapeHtml(title)}</span>
    </li>`
  })
  const more =
    results.length > maxList
      ? `<p class="text-xs text-gray-500 mt-2">+ ${results.length - maxList} more results (see raw JSON).</p>`
      : ''

  return `
    <div class="langchain-report-digest space-y-1">
      <div class="flex flex-wrap items-center gap-2 mb-4">
        <span class="text-lg font-semibold text-gray-900 dark:text-white">Pipeline results</span>
        <span class="rounded-full px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">${escapeHtml(String(data.pipeline))}</span>
      </div>
      <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">${escapeHtml(String(data.generated_at || '—'))}</p>

      ${card(
        'NBIB parsing',
        statGrid([
          ['Records parsed', fmtNum(nb.records_parsed)],
          ['Records skipped', fmtNum(nb.records_skipped)],
          ['Articles created', fmtNum(nb.articles_created)],
          ['Parse warnings', fmtNum(Array.isArray(nb.parse_warnings) ? nb.parse_warnings.length : nb.parse_warnings || 0)],
        ])
      )}

      ${card(
        'Screening',
        statGrid([
          ['Articles screened', fmtNum(sc.articles_screened != null ? sc.articles_screened : results.length)],
          ['Result rows', fmtNum(results.length)],
          ['Skipped articles', fmtNum(skipped.length)],
        ])
      )}

      ${card('Criteria', statGrid([['Inclusion items', fmtNum(inc)], ['Exclusion items', fmtNum(exc)]]))}

      ${
        results.length
          ? card(
              `Decisions (first ${Math.min(maxList, results.length)} of ${results.length})`,
              `<ol class="list-none pl-0 m-0">${listItems.join('')}</ol>${more}`
            )
          : ''
      }

      ${
        skipped.length
          ? card(
              'Skipped articles',
              `<pre class="text-xs overflow-x-auto whitespace-pre-wrap break-words bg-amber-50 dark:bg-amber-900/20 p-2 rounded max-h-48 overflow-y-auto">${escapeHtml(JSON.stringify(skipped, null, 2))}</pre>`
            )
          : ''
      }
    </div>
  `
}

function rawJsonDetails(jsonString) {
  return `
    <details class="mt-6 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
      <summary class="cursor-pointer select-none px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/50 hover:bg-gray-100 dark:hover:bg-gray-800">
        Raw JSON
      </summary>
      <pre class="bg-gray-50 dark:bg-gray-900 p-3 text-xs font-mono text-gray-800 dark:text-gray-200 whitespace-pre overflow-x-auto max-h-[50vh] overflow-y-auto border-t border-gray-200 dark:border-gray-600"><code>${escapeHtml(jsonString)}</code></pre>
    </details>
  `
}

function renderDigestHTML(data, prettyJson) {
  let body = ''
  if (isDigestibleExecutionReport(data)) {
    body = renderExecutionReportV1(data)
  } else if (isDigestiblePipelineResults(data)) {
    body = renderPipelineResultsNbib(data)
  } else {
    return null
  }
  return `<div class="langchain-json-digest-root">${body}${rawJsonDetails(prettyJson)}</div>`
}

function isDigestible(data) {
  return isDigestibleExecutionReport(data) || isDigestiblePipelineResults(data)
}

if (typeof window !== 'undefined') {
  window.LangchainExecutionReportDrawer = {
    isDigestible,
    renderHTML: renderDigestHTML,
    prepareJsonDrawerPayload,
  }
}

export {
  isDigestible,
  renderDigestHTML,
  isDigestibleExecutionReport,
  isDigestiblePipelineResults,
  prepareJsonDrawerPayload,
}
